import { inject, Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { unsubscribeAll } from 'src/app/common/app.helpers';
import { FileManagerService } from 'src/app/components/files/file-manager.service';
import { FieldAttribute, LoadFileContent } from 'src/app/interfaces/form-and-field.interface';
import { NotificationContextAttributesService } from 'src/app/notifications/notifications-context-attributes.service';
import { NotificationService } from 'src/app/notifications/notifications.service';
import { OnFileUploaded } from '../../../../interfaces/file.interface';
import { FileUploadStrategy, MultipleFileUpload, SingleFileUploadLookup } from './file-upload.strategy';

@Injectable()
export class FileUploadDelegate implements OnDestroy {
  private fileChanges$: Subject<OnFileUploaded> = new Subject<OnFileUploaded>();
  private folderCreated$: Subject<OnFileUploaded> = new Subject<OnFileUploaded>();
  private onError$: Subject<OnFileUploaded> = new Subject<OnFileUploaded>();

  private fileUploadStrategy: FileUploadStrategy;
  private subscriptions$: Subscription[] = [];

  private fileManagerService = inject(FileManagerService);
  private notificationService = inject(NotificationService);
  private modalAttributesService = inject(NotificationContextAttributesService);
  public constructor() {
    this.subscribeOnFileManagerChanges();
  }

  public get onErrorOccurred(): Observable<OnFileUploaded> {
    return this.onError$.asObservable();
  }

  public get onFileChanged(): Observable<OnFileUploaded> {
    return this.fileChanges$.asObservable();
  }

  public get onFolderCreated(): Observable<OnFileUploaded> {
    return this.folderCreated$.asObservable();
  }

  public ngOnDestroy(): void {
    if (this.onError$) {
      this.onError$.complete();
    }

    if (this.fileChanges$) {
      this.fileChanges$.complete();
    }

    if (this.folderCreated$) {
      this.folderCreated$.complete();
    }

    unsubscribeAll(this.subscriptions$);
  }

  /**
   * @description Checks file's existence and sets up the file-upload strategy.
   *              In case invalid data - emits error event.
   * @param { LoadFileContent } data Contains upload event, control name and type.
   */
  public uploadFile(data: LoadFileContent): void {
    if (data.event) {
      const target: HTMLInputElement = data.event.target as HTMLInputElement;

      if (target.files) {
        this.setStrategy(this.choseStrategy(data.attr));

        this.uploadFileByStrategy(data, target.files);
      }

      return;
    }

    this.onError$.next({
      link: '',
      controlName: data.attr.backendAttribute.name,
      fileName: '',
    });
  }

  /**
   * @description Sets the specified strategy to fileUploadStrategy object.
   * @param { FileUploadStrategy } strategy
   */
  private setStrategy(strategy: FileUploadStrategy): void {
    this.fileUploadStrategy = strategy;
  }

  /**
   * @description Delegates file uploading process to the fileUpload strategy.
   * @param { LoadFileContent } data
   * @param { HTMLInputElement } target
   */
  private uploadFileByStrategy(data: LoadFileContent, fileList: FileList): void {
    this.fileUploadStrategy.uploadFile(data, Array.from(fileList));
  }

  /**
   * @description Returns new instance of file-upload strategy depend on multiple
   *              property value.
   * @param { FieldAttribute } attr
   * @returns { FileUploadStrategy }
   */
  private choseStrategy(attr: FieldAttribute): FileUploadStrategy {
    if (attr.multiple) {
      return new MultipleFileUpload(this.fileManagerService);
    }

    const {
      backendAttribute: { value_class_front },
    } = attr;

    return new SingleFileUploadLookup[value_class_front](
      this.onError$,
      this.fileManagerService,
      this.notificationService,
      this.modalAttributesService,
    );
  }

  /**
   * @description Subscribes on onFileLoaded and onFolderCreated subjects, sends link
   *              to subscribers.
   */
  private subscribeOnFileManagerChanges(): void {
    this.subscriptions$.push(
      this.fileManagerService.onFileLoaded.subscribe((content: OnFileUploaded) => {
        this.processFileManagerChanges(content, this.fileChanges$);
      }),
    );

    this.subscriptions$.push(
      this.fileManagerService.onFolderCreated.subscribe((content: OnFileUploaded) => {
        this.processFileManagerChanges(content, this.folderCreated$);
      }),
    );
  }

  /**
   * @description Shared method for processing file manager changes.
   *      1. If the content contains not empty link, notifies subscribers about that
   *         via subject.
   *      2. Otherwise notifies subscribers about error.
   * @param { OnFileUploaded } content
   * @param { ubject<OnFileUploaded> } subject The subject to notify subscribers about
   *                                           successful link receiving.
   */
  private processFileManagerChanges(content: OnFileUploaded, subject: Subject<OnFileUploaded>): void {
    content.link ? subject.next(content) : this.onError$.next(Object.assign(content, { link: '' }));
  }
}
